import { useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ViewDocumentDialog from "../DocumentDialogs/ViewDocumentDialog";
import UpdateDocumentDialog from "../DocumentDialogs/UpdateDocumentDialog";
import { IdentifyingDocument } from "../../../types/professional";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

/** Matches the rows the data screens already draw, so this sits among them. */
const labelSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary
};

const valueSx = {
  ...typography.body.lSemibold,
  color: semanticColors.text.primary
};

/** The action sits level with what it acts on, as it does on mobile. */
const headerSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  mb: "4px"
};

const valueRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  flexWrap: "wrap"
};

/**
 * One identifying document on a data screen.
 *
 * Both actions are links rather than buttons — they sit inside a reading
 * surface, not a form, and giving them a button's weight would pull the eye
 * away from the data itself.
 *
 * The row shows even when nothing is registered yet: the professional still has
 * a licence number, and hiding the row would leave no way to file the card. With
 * nothing on record there is nothing to view, so only the action to add it is
 * offered.
 */
export default function DocumentRow({
  label,
  document,
  professional,
  type,
  fallbackNumber,
  viewTitle,
  updateTitle,
  addTitle
}: {
  label: string;
  /** Absent until the first one is filed. */
  document?: IdentifyingDocument;
  professional: boolean;
  type: number;
  /** The number already known from the profile, used when filing the first one. */
  fallbackNumber?: string;
  viewTitle: string;
  updateTitle: string;
  addTitle: string;
}) {
  const { t } = useTranslation();
  const [viewing, setViewing] = useState(false);
  const [editing, setEditing] = useState(false);

  const number = document?.documentId || fallbackNumber || "-";

  return (
    <>
      <Box sx={headerSx}>
        <Typography sx={labelSx}>{label}</Typography>
        <Link component="button" type="button" onClick={() => setEditing(true)}>
          {document
            ? t("identifyingDocuments.update")
            : t("identifyingDocuments.add")}
        </Link>
      </Box>

      <Box sx={valueRowSx}>
        <Typography sx={valueSx}>{number}</Typography>
        {document && (
          <Link
            component="button"
            type="button"
            onClick={() => setViewing(true)}
          >
            {t("identifyingDocuments.viewCard")}
          </Link>
        )}
      </Box>

      {document && (
        <ViewDocumentDialog
          open={viewing}
          onClose={() => setViewing(false)}
          title={viewTitle}
          document={document}
        />
      )}

      <UpdateDocumentDialog
        open={editing}
        onClose={() => setEditing(false)}
        title={document ? updateTitle : addTitle}
        document={document}
        professional={professional}
        type={type}
        defaultNumber={fallbackNumber}
        onUpdated={() => setEditing(false)}
      />
    </>
  );
}
